<template>
  <BaseModal
    :heading="$t('admin.treatwell.iban_modal.title')"
    useForm
    :loading="loading"
    :parentRoute="{ name: 'treatwell-admin' }"
    @submit="submit"
  >
    <Flex direction="column">
      <BaseHeading>{{ $t('admin.treatwell.iban_modal.heading') }}</BaseHeading>
      <BaseText>{{ $t('admin.treatwell.iban_modal.description') }}</BaseText>

      <div
        v-for="i in ['no_shows', 'payments', 'reverseable']"
        :key="i"
        :class="$style.bulletPoint"
      >
        <BaseIcon
          name="check"
          color="success"
          size="l"
        />
        {{ $t(`admin.treatwell.iban_modal.reasons.${i}`) }}
      </div>

      <div v-html="learnMoreMessage" />

      <BaseInput
        v-model="bankingDetails.accountHolderName"
        :label="$t('treatwell.bank_details.account_holder')"
        :disabled="treatwellStatus.bankDetailsProvided"
        v-test="'treatwell-account-holder-input'"
      />
      <BaseInput
        v-model="bankingDetails.bankName"
        :label="$t('treatwell.bank_details.bank_name')"
        :disabled="treatwellStatus.bankDetailsProvided"
        v-test="'treatwell-bank-name-input'"
      />
      <BaseInput
        v-model="bankingDetails.accountNumber"
        :label="isUk ? $t('treatwell.bank_details.account_number_uk') : $t('treatwell.bank_details.account_number')"
        :disabled="treatwellStatus.bankDetailsProvided"
        v-test="'treatwell-account-number-input'"
      />
      <BaseInput
        v-model="bankingDetails.bankCode"
        :label="isUk ? $t('treatwell.bank_details.bank_code_uk') : $t('treatwell.bank_details.bank_code')"
        :disabled="treatwellStatus.bankDetailsProvided"
        v-test="'treatwell-bank-code-input'"
      />
      <BaseText
        v-if="!isUk"
        size="s"
        iconBefore="info"
      >
        {{ $t('admin.treatwell.iban_modal.bic_explanation') }}
      </BaseText>
      <BaseText
        v-if="bankError"
        color="error"
      >
        {{ bankError }}
      </BaseText>
      <BaseAlert
        v-if="treatwellStatus.bankDetailsProvided"
        color="warning"
        :text="$t('admin.treatwell.iban_modal.iban_already_set')"
      />
    </Flex>

    <template #footer>
      <BaseButton
        submitForm
        :disabled="treatwellStatus.bankDetailsProvided"
        :loading="isSaving || isSavingRequest"
        v-test="'treatwell-iban-submit'"
      >
        {{ $t('global.actions.submit') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import { useMutation, useQuery } from '@vue/apollo-composable';
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import eventBus from '@/event-bus';
import { helpCenterArticleUrl } from '@/helpers/help-center';
import { flash } from '@/helpers/ui';
import { useCompanyStore } from '@/stores/company';
import { useTreatwellStore } from '@/stores/treatwell';
import type { TreatwellBankDetails } from '@/types';
import {
  GET_CURRENT_INTEGRATION_REQUEST,
  GET_TREATWELL_VENUE,
  TREATWELL_INTEGRATION_REQUEST_UPDATE_BANKING_DETAILS,
  UPDATE_TREATWELL_VENUE,
} from '../graphql';

const router = useRouter();
const { t } = useI18n();

const { treatwellStatus, getTreatwellStatus } = useTreatwellStore();
const { isUk } = useCompanyStore();

const bankingDetails = reactive<TreatwellBankDetails>({
  accountHolderName: '',
  accountNumber: '',
  bankCode: '',
  bankName: '',
});

const bankError = ref('');
const hasTreatwellVenue = ref(false);

const { loading, onResult: onVenueResult } = useQuery(GET_TREATWELL_VENUE);
onVenueResult(({ data: { treatwellVenue: venue } }) => {
  if (venue) {
    hasTreatwellVenue.value = true;
    if (venue.bankAccountHolder) {
      bankingDetails.accountHolderName = venue.bankAccountHolder;
      bankingDetails.accountNumber = venue.maskedBankAccountNumber;
      bankingDetails.bankCode = venue.bankCode;
      bankingDetails.bankName = venue.bankName;
    }
  } else {
    const { onResult: onIntegrationRequest } = useQuery(
      GET_CURRENT_INTEGRATION_REQUEST,
    );

    onIntegrationRequest(
      ({
        data: { treatwellCurrentIntegrationRequest: integrationRequest },
      }) => {
        bankingDetails.accountHolderName = integrationRequest.accountHolderName;
        bankingDetails.accountNumber = integrationRequest.maskedAccountNumber;
        bankingDetails.bankCode = integrationRequest.bankCode;
        bankingDetails.bankName = integrationRequest.bankName;
      },
    );
  }
});

const {
  mutate: updateBankingDetailsOnVenue,
  onDone,
  loading: isSaving,
} = useMutation(UPDATE_TREATWELL_VENUE, {
  variables: {
    input: {
      bankingDetails,
    },
  },
});

const {
  mutate: updateBankingDetailsOnRequest,
  onDone: onRequestDone,
  loading: isSavingRequest,
} = useMutation(TREATWELL_INTEGRATION_REQUEST_UPDATE_BANKING_DETAILS, {
  variables: {
    input: {
      bankingDetails,
    },
  },
});

const submit = () => {
  if (hasTreatwellVenue.value) {
    updateBankingDetailsOnVenue().then(() => getTreatwellStatus());
  } else {
    updateBankingDetailsOnRequest();
  }
};

onDone(({ data }) => {
  if (data.updateTreatwellVenue.errors) {
    bankError.value = t('admin.treatwell.iban_modal.error');
  } else {
    afterSave();
  }
});

onRequestDone(({ data }) => {
  if (data.treatwellIntegrationRequestUpdateBankingDetails.errors) {
    bankError.value = t('admin.treatwell.iban_modal.error');
  } else {
    afterSave();
  }
});

const afterSave = () => {
  flash(
    t(isUk ? 'global.flash.bank_details_saved' : 'global.flash.iban_saved'),
  );
  eventBus.$emit('update-task', 'provide_bank');
  treatwellStatus.bankDetailsProvided = true;
  router.push({
    name: 'treatwell-admin',
  });
};

const learnMoreMessage = computed(() => {
  const helpUrl = `<a href="${helpCenterArticleUrl(t('admin.treatwell.iban_modal.learn_more.help.url'))}">${t('admin.treatwell.iban_modal.learn_more.help.label')}</a>`;
  const supportUrl = `<a href="${t('admin.treatwell.iban_modal.learn_more.support.url')}">${t('admin.treatwell.iban_modal.learn_more.support.label')}</a>`;
  return t('admin.treatwell.iban_modal.learn_more.explanation', {
    helpUrl,
    supportUrl,
  });
});
</script>

<style
  lang="scss"
  module
>
.bulletPoint {
  display: flex;
  align-items: center;
  gap: $spacing * 0.5;
}
</style>
